/** Cash-buyers list (v1 buyers-agent.js). Your dispo rolodex: who to send deals
 * to. Stored in the BUYERS org-settings blob (whitelisted in PHASE22, already
 * run — no new migration). Whole-array replace through the settings door, same
 * pattern as investors. The SMS blast that consumes this list is a separate
 * gated (Quo) round; this restores the list + its management first. */
import type { RecOp } from "../../lib/outbox";
import { withLedgerBase } from "../settings/api";

export interface Buyer {
  id: string;
  n: string; // name
  co: string; // company
  type: string; // BUYER_TYPES
  status: string; // BUYER_STATUS
  area: string; // buy-box area
  ph: string;
  em: string;
  min: number; // price range floor
  max: number; // price range ceiling (0 = no cap)
  state: string;
  tags: string[];
  ph2: string; // secondary phone
  em2: string; // secondary email
  counties: string; // target counties (free text)
  cities: string; // target cities (free text)
  financing: string; // BUYER_FINANCING ("" = unset)
  exit: string; // BUYER_EXIT ("" = unset)
  notes: string; // free-text notes
}

export const BUYER_TYPES = ["Cash Buyer", "Hard Money", "Agent", "Fix & Flip", "Buy & Hold", "Wholesaler"];
export const BUYER_STATUS = ["Active", "VIP", "Inactive", "Do Not Contact"];
export const BUYER_FINANCING = ["Cash", "Hard Money", "Conventional", "IRA", "Other"];
export const BUYER_EXIT = ["Fix & Flip", "Rental", "BRRRR", "Buy & Hold", "Other"];

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const num = (v: unknown): number => { const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^0-9.-]/g, "")); return Number.isFinite(n) ? n : 0; };

let _bid = 0;
export const buyerId = (): string => "b" + Date.now().toString(36) + (_bid++).toString(36);

export function narrowBuyer(raw: unknown): Buyer {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const tags = o["tags"];
  return {
    id: str(o["id"]) || buyerId(),
    n: str(o["n"] ?? o["name"]),
    co: str(o["co"] ?? o["company"]),
    type: str(o["type"]) || "Cash Buyer",
    status: str(o["status"]) || "Active",
    area: str(o["area"]),
    ph: str(o["ph"] ?? o["phone"]),
    em: str(o["em"] ?? o["email"]),
    min: num(o["min"]),
    max: num(o["max"]),
    state: str(o["state"]),
    tags: Array.isArray(tags) ? tags.map(str).filter(Boolean) : [],
    ph2: str(o["ph2"] ?? o["phone2"]),
    em2: str(o["em2"] ?? o["email2"]),
    counties: str(o["counties"]),
    cities: str(o["cities"]),
    financing: str(o["financing"]),
    exit: str(o["exit"]),
    notes: str(o["notes"]),
  };
}

export function allBuyers(orgSettings: Record<string, unknown>): Buyer[] {
  const raw = orgSettings["BUYERS"];
  return Array.isArray(raw) ? raw.map(narrowBuyer) : [];
}

/** Whole-array replace through the settings door (v1 persistState → BUYERS). */
export function opPutBuyers(all: Buyer[]): RecOp {
  return withLedgerBase({ s: "settings", op: "put", k: "BUYERS", d: all as unknown as Record<string, unknown>[] });
}

/** v1 priceRange: "$120k – $260k", "$0 – ∞", or "—" when unset. */
export function priceRange(b: Buyer): string {
  if (!b.max && !b.min) return "—";
  const f = (n: number) => "$" + Math.round(n / 1000) + "k";
  return (b.min ? f(b.min) : "$0") + " – " + (b.max ? f(b.max) : "∞");
}

/** v1 getBuyers filter: free-text over name/co/area/phone/email + type/status,
 * plus v2 state + tag facets (Task #41). state/tag default to "all". */
export function filterBuyers(buyers: Buyer[], f: { q: string; type: string; status: string; state?: string; tag?: string }): Buyer[] {
  let r = buyers;
  const q = f.q.trim().toLowerCase();
  if (q) r = r.filter((b) => (b.n + " " + b.co + " " + b.area + " " + b.ph + " " + b.em + " " + b.tags.join(" ")).toLowerCase().includes(q));
  if (f.type !== "all") r = r.filter((b) => b.type === f.type);
  if (f.status !== "all") r = r.filter((b) => b.status === f.status);
  if (f.state && f.state !== "all") r = r.filter((b) => b.state === f.state);
  if (f.tag && f.tag !== "all") r = r.filter((b) => b.tags.includes(f.tag as string));
  return r;
}

/** Distinct non-empty states across the list, sorted (for the state facet). */
export function buyerStates(buyers: Buyer[]): string[] {
  return [...new Set(buyers.map((b) => b.state).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}
/** Distinct tags across the list, sorted (for the tag facet + suggestions). */
export function buyerTags(buyers: Buyer[]): string[] {
  return [...new Set(buyers.flatMap((b) => b.tags))].filter(Boolean).sort((a, b) => a.localeCompare(b));
}

export const statusHue = (status: string): string =>
  status === "VIP" ? "violet" : status === "Active" ? "emerald" : status === "Do Not Contact" ? "red" : "slate";

// ---- CSV export / import (v1 exportBuyersCSV; v1's import was a stub) --------
const CSV_COLS = ["Name", "Company", "Type", "Status", "Phone", "Email", "Buy Box Area", "Price Min", "Price Max", "State", "Tags"] as const;
const csvCell = (s: string): string => `"${String(s).replace(/"/g, '""')}"`;

/** Serialize buyers to a CSV string (header + quoted rows). Tags are ;-joined. */
export function buyersToCsv(buyers: Buyer[]): string {
  const rows = buyers.map((b) => [b.n, b.co, b.type, b.status, b.ph, b.em, b.area, b.min ? String(b.min) : "", b.max ? String(b.max) : "", b.state, b.tags.join(";")].map(csvCell).join(","));
  return [CSV_COLS.join(","), ...rows].join("\n");
}

/** Parse a CSV (as written by buyersToCsv, or a compatible header order) into
 * buyers. Tolerant: quoted fields with commas/newlines, ""-escapes, missing
 * trailing columns. Column order is taken from the header row when present. */
export function csvToBuyers(csv: string): Buyer[] {
  const records = parseCsv(csv);
  if (!records.length) return [];
  const header = records[0]!.map((h) => h.trim().toLowerCase());
  const idx = (name: string) => header.indexOf(name.toLowerCase());
  const iName = idx("name"), iCo = idx("company"), iType = idx("type"), iStatus = idx("status"),
    iPh = idx("phone"), iEm = idx("email"), iArea = idx("buy box area"),
    iMin = idx("price min"), iMax = idx("price max"), iState = idx("state"), iTags = idx("tags");
  const hasHeader = iName >= 0 || iEm >= 0;
  const body = hasHeader ? records.slice(1) : records;
  const at = (row: string[], i: number) => (i >= 0 && i < row.length ? (row[i] ?? "").trim() : "");
  const out: Buyer[] = [];
  for (const row of body) {
    if (!row.some((c) => c.trim())) continue; // skip blank lines
    const tagsRaw = iTags >= 0 ? at(row, iTags) : "";
    out.push(narrowBuyer({
      n: hasHeader ? at(row, iName) : (row[0] ?? ""),
      co: hasHeader ? at(row, iCo) : (row[1] ?? ""),
      type: hasHeader ? at(row, iType) : (row[2] ?? ""),
      status: hasHeader ? at(row, iStatus) : (row[3] ?? ""),
      ph: hasHeader ? at(row, iPh) : (row[4] ?? ""),
      em: hasHeader ? at(row, iEm) : (row[5] ?? ""),
      area: hasHeader ? at(row, iArea) : "",
      min: hasHeader ? at(row, iMin) : "",
      max: hasHeader ? at(row, iMax) : "",
      state: hasHeader ? at(row, iState) : "",
      tags: tagsRaw ? tagsRaw.split(/[;,]/).map((t) => t.trim()).filter(Boolean) : [],
    }));
  }
  return out;
}

/** Minimal RFC-4180-ish CSV parser: handles quoted fields, ""-escapes, and
 * commas/newlines inside quotes. Returns an array of rows (arrays of cells). */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQ = false;
  const s = text.replace(/\r\n?/g, "\n");
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQ) {
      if (c === '"') { if (s[i + 1] === '"') { cell += '"'; i++; } else inQ = false; }
      else cell += c;
    } else if (c === '"') inQ = true;
    else if (c === ",") { row.push(cell); cell = ""; }
    else if (c === "\n") { row.push(cell); rows.push(row); row = []; cell = ""; }
    else cell += c;
  }
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return rows;
}
